import { Alert, Box, Button, Dialog, LinearProgress, Stack, Typography } from '@mui/material'
import { useState } from 'react'

type MigrationState =
  | { step: 'idle' }
  | { step: 'file-selected'; file: File }
  | { step: 'validated'; fileId: string }
  | { step: 'migrating'; fileId: string }

export type BulkMigrationProps = {
  onUpload: (file: File) => Promise<{ fileId: string }>
  onStartMigration: (fileId: string) => Promise<void>
  onClose?: () => void
  migrationStatus: { pending: number; completed: number; failed: number } | undefined
  isUploading: boolean
  isStarting: boolean
}

export const BulkMigration = ({ onUpload, onStartMigration, migrationStatus, isUploading, isStarting, onClose }: BulkMigrationProps) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [state, setState] = useState<MigrationState>({ step: 'idle' })
  const [validationErrors, setValidationErrors] = useState<string[]>([])

  const isMigrating = state.step === 'migrating'
  const total = migrationStatus ? migrationStatus.pending + migrationStatus.completed + migrationStatus.failed : 0
  const progress = total > 0 ? ((migrationStatus!.completed + migrationStatus!.failed) / total) * 100 : 0
  const isDone = isMigrating && migrationStatus ? migrationStatus.pending === 0 && total > 0 : false

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setState({ step: 'file-selected', file })
      setValidationErrors([])
    }
  }

  const handleUpload = async () => {
    if (state.step !== 'file-selected') return

    try {
      setValidationErrors([])
      const { fileId } = await onUpload(state.file)
      setState({ step: 'validated', fileId })
    } catch (err) {
      const errors = extractValidationErrors(err)
      setValidationErrors(errors)
      setState({ step: 'idle' })
    }
  }

  const handleStartMigration = async () => {
    if (state.step !== 'validated') return

    try {
      setState({ step: 'migrating', fileId: state.fileId })
      await onStartMigration(state.fileId)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start migration'
      setValidationErrors([message])
      setState({ step: 'validated', fileId: state.fileId })
    }
  }

  const handleClose = () => {
    if (isMigrating && !isDone) return
    setDialogOpen(false)
    onClose?.()
  }

  const reset = () => {
    setState({ step: 'idle' })
    setValidationErrors([])
  }

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setDialogOpen(true)}>
        Upload CSV
      </Button>
      <Dialog open={dialogOpen} onClose={handleClose} slotProps={{ transition: { onExited: reset } }} maxWidth="sm" fullWidth>
        <Stack
          sx={{
            gap: 3,
            padding: 3,
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h3">Bulk template migration</Typography>
          </Stack>
          <Stack>
            {!isMigrating && state.step !== 'validated' && (
              <Stack
                sx={{
                  gap: 1,
                  alignItems: 'stretch',
                }}
              >
                <Button variant="outlined" component="label">
                  Select CSV file
                  <input type="file" accept=".csv" hidden onChange={handleFileChange} />
                </Button>
                {state.step === 'idle' && (
                  <Stack
                    sx={{
                      paddingTop: 1,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      or
                    </Typography>
                    <Button component="a" href="/templates/bulk-migration.csv" download>
                      Download template
                    </Button>
                  </Stack>
                )}
              </Stack>
            )}

            {state.step === 'validated' && <Alert severity="success">File validated successfully. Ready to start migration.</Alert>}

            {validationErrors.length > 0 && (
              <Alert severity="error">
                <Typography variant="body2" gutterBottom fontWeight="bold">
                  Validation failed:
                </Typography>
                <ul>
                  {validationErrors.map((error) => (
                    <li key={error}>
                      <Typography variant="body2">{error}</Typography>
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {isMigrating && migrationStatus && (
              <Stack
                sx={{
                  gap: 1,
                }}
              >
                <Stack
                  sx={{
                    gap: 0.5,
                  }}
                >
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="p4Regular">
                    Completed: {migrationStatus.completed} | Failed: {migrationStatus.failed} | Pending: {migrationStatus.pending}
                  </Typography>
                </Stack>
                {isDone && (
                  <Alert severity={migrationStatus.failed > 0 ? 'warning' : 'success'} sx={{ mt: 1 }}>
                    Migration finished. {migrationStatus.completed} completed, {migrationStatus.failed} failed.
                  </Alert>
                )}
              </Stack>
            )}
          </Stack>
          {!isDone && (
            <>
              {state.step === 'file-selected' && (
                <Button variant="contained" onClick={handleUpload} loading={isUploading} disabled={isUploading}>
                  {isUploading ? 'Uploading...' : `Upload ${state.file.name}`}
                </Button>
              )}
              {state.step === 'validated' && (
                <Button variant="contained" onClick={handleStartMigration} disabled={isStarting}>
                  {isStarting ? 'Starting...' : 'Start migration'}
                </Button>
              )}
            </>
          )}
        </Stack>
      </Dialog>
    </>
  )
}

function extractValidationErrors(err: unknown): string[] {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const response = (err as { response?: { data?: { message?: string } } }).response
    const message = response?.data?.message
    if (message) {
      const prefix = 'Migration validation failed: '
      const body = message.startsWith(prefix) ? message.slice(prefix.length) : message
      return body.split(/,\s*(?=Row\s)/).map((s) => s.trim())
    }
  }
  if (err instanceof Error) return [err.message]
  return ['Upload failed']
}
